import { createContext, useCallback, useMemo, type ReactNode } from "react";

import UFOSegment from "@atlaskit/react-ufo/segment";

import {
    useHomePageJPDXflowTargetingCohort,
    useHomePageJSMXflowTargetingCohort,
    useHomePageLoomXflowTargetingCohort,
    useLoggedInHasJiraAndNotConfluence,
    useLoggedOut,
    useMlBasedXflowRecommendedProduct
} from "@/components/user-context";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";
import { GlobalFooter } from "@/patterns/global-footer/src";
import GlobalNav from "@/patterns/global-nav/src";
import { useExperiment } from "@/utils/experiment";
import { experiments } from "@/utils/experiment/manifest";
import { FeatureExposed } from "@/utils/feature-exposed";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { ParentScrollTracking } from "@/utils/scroll-tracking";

import StickyContainer from "../../../../patterns/sticky-container/sticky-container";

import {
    FutureOfTeamwork,
    CustomerStories,
    GetStartedCTA,
    HeroJiraCtaTemplate,
    ScrollPinnedSections,
    TabsAndIntegrationsSections,
    HomePageBannerAlert,
    TeamStats,
    ConfluenceCrossflowCTA,
    LoggedInHeroContainer,
    BelowNavBannerAlert,
    TeamworkCollectionHero,
    AtlassianIntelligence
} from "./sections-v2";
import { HeroTakeoverTeam26 } from "./sections-v2/hero-takeover-team-26";
import { HeroTWCHeader } from "./sections-v2/hero-twc-header";
import { HeroTeamworkCollection } from "./sections-v2/hero-twc-tabs";
import { JiraLoggedInHeader } from "./sections-v2/jira-logged-in-header";
import { JPDCrossflowComponent } from "./sections-v2/jpd-xflow-hero/homepage-jpd-xflow";
import { ContentTypesSection } from "./sections-v2/jsm-xflow-hero/content-types-section";
import { JSMCrossflowComponent } from "./sections-v2/jsm-xflow-hero/homepage-jsm-xflow";
import { LoomCrossflowComponent } from "./sections-v2/loom-xflow-hero/homepage-loom-xflow";
import { ProductProvider } from "./sections-v2/reusable-xflow-hero-component/product-context";
import { RovoInnovationsVideo } from "./sections-v2/rovo-innovations-video";

export const HeroSelectedTeamIndexContext = createContext<number | undefined>(
    undefined
);

type CrossflowProduct = "jsm" | "jpd" | "loom" | "confluence";

function getHomePageQueryParams(): URLSearchParams | null {
    if (typeof window !== "undefined") {
        return new URLSearchParams(window.location.search);
    }
    if (globalThis.location) {
        return new URLSearchParams(globalThis.location.search);
    }
    return null;
}

/**
 * Determines which targeting cohort is enabled and returns the corresponding product.
 * Checks JSM first, then JPD, then Loom.
 */
const useGetEnabledCrossflowProduct = (): CrossflowProduct | null => {
    const isConfEnabled = useLoggedInHasJiraAndNotConfluence();
    const isJsmEnabled = useHomePageJSMXflowTargetingCohort();
    const isJpdEnabled = useHomePageJPDXflowTargetingCohort();
    const isLoomEnabled = useHomePageLoomXflowTargetingCohort();

    return useMemo(() => {
        if (isConfEnabled) return "confluence";
        if (isJsmEnabled) return "jsm";
        if (isJpdEnabled) return "jpd";
        if (isLoomEnabled) return "loom";
        return null;
    }, [isConfEnabled, isJsmEnabled, isJpdEnabled, isLoomEnabled]);
};

/**
 * Default Jira CTA hero shown when user is not enrolled in any crossflow experiments
 */
const DefaultJiraHero = ({
    gwp10965XflowRecommendation = false,
    showJiraPlusRovoHomepageChanges = false
}: {
    gwp10965XflowRecommendation?: boolean;
    showJiraPlusRovoHomepageChanges?: boolean;
} = {}) => (
    <HeroJiraCtaTemplate
        showJiraPlusRovoHomepageChanges={showJiraPlusRovoHomepageChanges}
    >
        <JiraLoggedInHeader
            gwp10965XflowRecommendation={gwp10965XflowRecommendation}
            showJiraPlusRovoHomepageChanges={showJiraPlusRovoHomepageChanges}
        />
    </HeroJiraCtaTemplate>
);

/** Maps ML recommendation string to CrossflowProduct for hero selection. */
function mapRecommendedProductToCrossflow(
    product: string
): CrossflowProduct | null {
    const n = product.toLowerCase().trim();
    if (n === "confluence") return "confluence";
    if (n === "jira service management") return "jsm";
    if (n === "jira product discovery") return "jpd";
    if (n === "loom") return "loom";
    return null;
}

/**
 * Renders crossflow hero for a given product key (from ML recommendation when experiment treatment is on).
 * Passes gwp10965 variant UI through to match cohort-based crossflow heroes.
 */
const renderHeroForCrossflowProduct = (
    product: CrossflowProduct | null,
    gwp10965XflowRecommendation: boolean
) => {
    switch (product) {
        case "confluence":
            return (
                <FeatureExposed
                    experimentKey="gwp-5831-homepage-confluence-xflow"
                    group="Test"
                >
                    <ProductProvider product="confluence">
                        <ConfluenceCrossflowCTA
                            contentComponent={ContentTypesSection}
                            gwp10965XflowRecommendation={
                                gwp10965XflowRecommendation
                            }
                        />
                    </ProductProvider>
                </FeatureExposed>
            );
        case "jsm":
            return (
                <ProductProvider product="jsm">
                    <JSMCrossflowComponent
                        contentComponent={ContentTypesSection}
                        gwp10965XflowRecommendation={
                            gwp10965XflowRecommendation
                        }
                    />
                </ProductProvider>
            );
        case "jpd":
            return (
                <ProductProvider product="jpd">
                    <JPDCrossflowComponent
                        contentComponent={ContentTypesSection}
                        gwp10965XflowRecommendation={
                            gwp10965XflowRecommendation
                        }
                    />
                </ProductProvider>
            );
        case "loom":
            return (
                <ProductProvider product="loom">
                    <LoomCrossflowComponent
                        contentComponent={ContentTypesSection}
                        gwp10965XflowRecommendation={
                            gwp10965XflowRecommendation
                        }
                    />
                </ProductProvider>
            );
        default:
            return (
                <DefaultJiraHero
                    gwp10965XflowRecommendation={gwp10965XflowRecommendation}
                />
            );
    }
};

/**
 * Only mounted for gwp10965 experiment treatment. Calls useMlBasedXflowRecommendedProduct and renders
 * the NBP-driven hero with variant UI (hook runs only when this component is mounted).
 */
const LoggedInHeroWithMLRecommendation = () => {
    const recommendedProduct = useMlBasedXflowRecommendedProduct();
    const crossflowProduct = recommendedProduct
        ? mapRecommendedProductToCrossflow(recommendedProduct)
        : null;
    return (
        <LoggedInHeroContainer>
            {renderHeroForCrossflowProduct(crossflowProduct, true)}
        </LoggedInHeroContainer>
    );
};

export const HomePageTemplate = () => {
    const isLoggedIn = !useLoggedOut();
    const enabledCrossflowProduct = useGetEnabledCrossflowProduct();
    const xflowRecommendationExperiment = useExperiment(
        experiments.gwp10965XflowRecommendation2NbpDriven
    );
    const gwp10965XflowRecommendation =
        xflowRecommendationExperiment.type === "enrolled" &&
        xflowRecommendationExperiment.value === true;

    const checkJiraPlusRovoHomepageChangesExperiment = useExperiment(
        experiments.gwp10906WacHomepageHeroJiraPlusRovoChanges
    );
    const showJiraPlusRovoHomepageChanges =
        checkJiraPlusRovoHomepageChangesExperiment.type === "enrolled" &&
        checkJiraPlusRovoHomepageChangesExperiment.value === true;

    const queryParams = getHomePageQueryParams();
    const isTeamchangerVariant =
        queryParams?.get("teamchangerReferral") === "true";

    const wacHomepageRovoVideoModuleUpdateGate = checkFeatureFlagGate(
        featureFlags.gwp10147WacHomepageRovoVideoModuleGate
    );

    const renderCrossflowHero = useCallback(() => {
        switch (enabledCrossflowProduct) {
            case "confluence":
                return (
                    <FeatureExposed
                        experimentKey="gwp-5831-homepage-confluence-xflow"
                        group="Test"
                    >
                        <ProductProvider product="confluence">
                            <ConfluenceCrossflowCTA
                                contentComponent={ContentTypesSection}
                            />
                        </ProductProvider>
                    </FeatureExposed>
                );
            case "jsm":
                return (
                    <ProductProvider product="jsm">
                        <JSMCrossflowComponent
                            contentComponent={ContentTypesSection}
                        />
                    </ProductProvider>
                );
            case "jpd":
                return (
                    <ProductProvider product="jpd">
                        <JPDCrossflowComponent
                            contentComponent={ContentTypesSection}
                        />
                    </ProductProvider>
                );
            case "loom":
                return (
                    <ProductProvider product="loom">
                        <LoomCrossflowComponent
                            contentComponent={ContentTypesSection}
                        />
                    </ProductProvider>
                );
            default:
                return <DefaultJiraHero />;
        }
    }, [enabledCrossflowProduct]);

    const heroSection: ReactNode = useMemo(() => {
        if (isTeamchangerVariant) {
            return (
                <HeroTeamworkCollection>
                    <HeroTWCHeader />
                </HeroTeamworkCollection>
            );
        }
        if (!isLoggedIn) {
            return (
                <DefaultJiraHero
                    showJiraPlusRovoHomepageChanges={
                        showJiraPlusRovoHomepageChanges
                    }
                />
            );
        }
        if (gwp10965XflowRecommendation) {
            return <LoggedInHeroWithMLRecommendation />;
        }
        return (
            <LoggedInHeroContainer>
                {renderCrossflowHero()}
            </LoggedInHeroContainer>
        );
    }, [
        isTeamchangerVariant,
        isLoggedIn,
        gwp10965XflowRecommendation,
        showJiraPlusRovoHomepageChanges,
        renderCrossflowHero
    ]);

    let rovoVideoOrIntelligenceSection: ReactNode;
    if (wacHomepageRovoVideoModuleUpdateGate) {
        rovoVideoOrIntelligenceSection = <RovoInnovationsVideo />;
    } else {
        rovoVideoOrIntelligenceSection = <AtlassianIntelligence />;
    }

    return (
        <UFOSegment name="home-page-v2">
            <div>
                <StickyContainer>
                    <HomePageBannerAlert />
                    <GlobalNav productKey={"home"} />
                    <BelowNavBannerAlert />
                </StickyContainer>
                <TypographyStyles>
                    <main id="content">
                        <ParentScrollTracking
                            scrollTrackingEnabled={true}
                            pageId="home-page-v2"
                        >
                            <HeroTakeoverTeam26 />
                            {heroSection}
                            {rovoVideoOrIntelligenceSection}
                            <TeamworkCollectionHero />
                            <ScrollPinnedSections />
                            <TeamStats />
                            <TabsAndIntegrationsSections />
                            <CustomerStories />
                            <FutureOfTeamwork />
                            <GetStartedCTA />
                        </ParentScrollTracking>
                    </main>
                </TypographyStyles>
                <GlobalFooter />
            </div>
        </UFOSegment>
    );
};
